// L120 Printing Labels — the former standalone `label-station` kiosk, integrated
// into the main app (TEST INTERFACES). Operator picks a length + operator, prints
// a ZPL label (with QR) on the Zebra, which also completes the active drum token.
// Self-contained + scoped under `.labelstation-root` so its dark kiosk CSS does
// not leak into the rest of the app. Talks to redis_dev (:5100) + WS (:8080).
import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import './labelStation.css';
import logoFinal from './logo_final.jpg';
import ceMarking from './ce-marking-logo.jpg';
import { API } from '../../config/api';
import { formatProductLabel } from '../../helpers/productLabel';
import { redisFetch } from '../../features/jobs/api/http';

const ENG_BASE = API.BACKEND;
const WS_URL = API.WS;
const APP_BASE = API.APP; // for the QR that opens project-details

// ── tiny API layer ──
//
// It used to mirror label-station/src/api.js with a bare `fetch`. That copy runs
// on the Pi, which has no login and no token; THIS one runs inside the web app,
// where :5100 requires one — so every call here came back 401 and the page sat
// empty. The tell was in the same file: `getOperators` below already sends the
// token, so the omission was only ever in this helper.
//
// `redisFetch` is the house client and attaches it. See the note on it in
// features/jobs/api/http.js for the two other ways a :5100 call loses its token.
async function req(url, options = {}) {
  const res = await redisFetch(url, options);
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

const api = {
  getLabelStatus: machineId => req(`/api/redis/label/status/${machineId}`),
  print: body =>
    req('/api/redis/label/print', { method: 'POST', body: JSON.stringify(body) }),
  reprint: body =>
    req('/api/redis/label/reprint', { method: 'POST', body: JSON.stringify(body) }),
  rawPrint: body =>
    req('/api/redis/label/raw-print', { method: 'POST', body: JSON.stringify(body) }),
  getOperators: () =>
    fetch(`${ENG_BASE}/api/v1/operators`, {
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': localStorage.getItem('token') || ''
      }
    }).then(r => r.json())
};

// ── live counter socket (mirrors label-station/src/useSocket.js) ──
function useMachineSocket(machineId) {
  const [meterAct, setMeterAct] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [status, setStatus] = useState('');
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!machineId) return undefined;
    const socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      // See providers/WebSocketProvider.js — no-op on the LAN, required outside.
      auth: { token: localStorage.getItem('token') || '' }
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('subscribe', { channelType: 'machine-single', machineId });
    });
    socket.on('disconnect', () => setConnected(false));

    const handleData = payload => {
      const state = payload?.state || payload;
      const plc = state?.currentPlcState || {};
      setMeterAct(Number(plc.METER_ACT || plc.meterAct || 0));
      setSpeed(Number(plc.LINE_SPEED || state?.speed || 0));
      setStatus(
        state?.status?.label ||
          (typeof state?.status === 'string' ? state.status : '')
      );
    };
    socket.on('machineSingleSnapshot', handleData);
    socket.on('machineSingleUpdate', handleData);

    return () => {
      socket.emit('unsubscribe');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [machineId]);

  return { meterAct, speed, status, connected };
}

const PHASE_LABELS = {
  conductor_isolation: 'Conductor Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior Sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Exterior Sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};

function OperatorSelect({ value, onChange }) {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .getOperators()
      .then(res => setOperators((res?.data || []).filter(op => op.active)))
      .catch(err => console.error('Failed to load operators:', err))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="operator-select">
      <select value={value} onChange={e => onChange(e.target.value)} disabled={loading}>
        <option value="">{loading ? 'Loading...' : 'Select Operator'}</option>
        {operators.map(op => (
          <option key={op._id} value={op.name}>
            {op.name} (Shift {op.shift})
          </option>
        ))}
      </select>
    </div>
  );
}

function ProductionInfo({ data }) {
  if (!data || !data.active) {
    return (
      <div className="production-info empty">
        <span className="no-production">No active production</span>
      </div>
    );
  }
  const productName = data.name
    ? formatProductLabel(data)
    : data.productLabel || 'N/A';
  const phaseName = PHASE_LABELS[data.phase] || data.phase || '-';
  const drumStr =
    data.drumMaterial && data.drumDiameter
      ? `${data.drumMaterial.charAt(0).toUpperCase() + data.drumMaterial.slice(1)} ${data.drumDiameter}mm`
      : '-';
  return (
    <div className="production-info">
      <div className="info-row main">
        <span className="product-name">{productName}</span>
        {data.color && (
          <span className="color-badge" data-color={String(data.color).toLowerCase()}>
            {data.color}
          </span>
        )}
      </div>
      <div className="info-row details">
        <div className="detail">
          <span className="label">Phase</span>
          <span className="value">{phaseName}</span>
        </div>
        <div className="detail">
          <span className="label">Drum</span>
          <span className="value">{drumStr}</span>
        </div>
        <div className="detail">
          <span className="label">Planned</span>
          <span className="value">{Number(data.plannedLength || 0).toLocaleString()} m</span>
        </div>
        <div className="detail">
          <span className="label">Job</span>
          <span className="value job-code">{data.jobCode || '-'}</span>
        </div>
      </div>
      {data.standard && (
        <div className="info-row standard">
          {data.standard.productStandard && (
            <span className="std-tag">{data.standard.productStandard}</span>
          )}
          {data.standard.performanceStandard && (
            <span className="std-tag">{data.standard.performanceStandard}</span>
          )}
          {data.standard.tension && <span className="std-tag">{data.standard.tension}</span>}
        </div>
      )}
    </div>
  );
}

function MeterCounter({ meterAct, adjustment, setAdjustment, speed }) {
  const intervalRef = useRef(null);
  const displayValue = (meterAct + adjustment).toFixed(1);
  const startHold = useCallback(
    delta => {
      setAdjustment(prev => prev + delta);
      intervalRef.current = setInterval(() => setAdjustment(prev => prev + delta), 150);
    },
    [setAdjustment]
  );
  const stopHold = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);
  return (
    <div className="meter-counter">
      <button
        className="arrow-btn up"
        onPointerDown={() => startHold(1)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
      >
        &#9650;
      </button>
      <div className="counter-display">
        <span className="counter-value">{displayValue}</span>
        <span className="counter-unit">m</span>
      </div>
      <button
        className="arrow-btn down"
        onPointerDown={() => startHold(-1)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
      >
        &#9660;
      </button>
      <div className="counter-meta">
        <span className="speed-display">
          {speed > 0 ? `${speed.toFixed(1)} m/min` : 'STOPPED'}
        </span>
        {adjustment !== 0 && (
          <button className="reset-adj" onClick={() => setAdjustment(0)}>
            Reset adjustment ({adjustment > 0 ? '+' : ''}
            {adjustment})
          </button>
        )}
      </div>
    </div>
  );
}

function PrintButton({ machineId, executedLen, operator, hasLastLabel, disabled, onPrinted }) {
  const [printing, setPrinting] = useState(false);
  const [reprinting, setReprinting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handlePrint = async () => {
    if (printing || disabled) return;
    setPrinting(true);
    setFeedback(null);
    try {
      const res = await api.print({ machineId, executedLen, operator });
      setFeedback({ type: 'success', msg: `Printed ${res.printed?.length || executedLen}m` });
      onPrinted?.();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message });
    } finally {
      setPrinting(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };
  const handleReprint = async () => {
    if (reprinting) return;
    setReprinting(true);
    setFeedback(null);
    try {
      await api.reprint({ machineId });
      setFeedback({ type: 'success', msg: 'Reprinted last label' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message });
    } finally {
      setReprinting(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };
  return (
    <div className="print-section">
      <div className="print-buttons">
        <button className="btn-print" onClick={handlePrint} disabled={printing || disabled}>
          {printing ? 'PRINTING...' : 'PRINT LABEL'}
        </button>
        <button className="btn-reprint" onClick={handleReprint} disabled={reprinting || !hasLastLabel}>
          {reprinting ? 'REPRINTING...' : 'REPRINT'}
        </button>
      </div>
      {feedback && <div className={`feedback ${feedback.type}`}>{feedback.msg}</div>}
    </div>
  );
}

function QueuePreview({ items = [] }) {
  if (!items.length) {
    return (
      <div className="queue-preview">
        <span className="queue-empty">Queue empty — no upcoming tokens</span>
      </div>
    );
  }
  const short = {
    conductor_isolation: 'Isolation',
    rope: 'Rope',
    interior_sheat: 'Int. Sheath',
    armouring: 'Armouring',
    exterior_sheat: 'Ext. Sheath',
    coiling: 'Coiling',
    rewinding: 'Rewinding'
  };
  return (
    <div className="queue-preview">
      <span className="queue-title">Queue ({items.length})</span>
      <div className="queue-items">
        {items.slice(0, 5).map((item, i) => (
          <div key={item.tokenId || i} className="queue-item">
            <span className="qi-index">{i + 1}</span>
            <span className="qi-length">{Number(item.plannedLength || 0).toLocaleString()}m</span>
            {item.color && (
              <span className="qi-color" data-color={String(item.color).toLowerCase()}>
                {item.color}
              </span>
            )}
            <span className="qi-phase">{short[item.phase] || item.phase || ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LABEL PREVIEW (WYSIWYG). The label is described as elements in printer-dot
// coordinates (812×609 = 100×75mm @203dpi) and scaled into the preview box. The
// real QR is rendered by the Zebra (^BQ); here we draw a cosmetic faux-QR so the
// operator sees where it sits. This mirrors the CURRENT backend ZPL
// (redis_dev/helpers/zplTemplate.js); once the final layout is decided we align both.
// ─────────────────────────────────────────────────────────────────────────────
const LABEL_W = 812, LABEL_H = 609;

// "Utilizare:" text per product family (wrapped to label width)
const USAGE = {
  energie: ['Alimentarea cu energie electrica la tensiunea', 'de 0.6/1kV - pentru cladiri si alte', 'constructii civile.'],
  casnic: ['Conectarea aparatelor electrice de uz casnic', 'si industrial la reteaua de alimentare fixa.'],
  rvk: ['Instalatii interior-exterior, conectare tablouri,', 'echipamente electrice, trasee de cablu cu flexiuni', 'complexe, medii umede, subterane, rezistenta UV.'],
  n2xh: ['Alimentare cu energie electrica, cladiri si alte', 'constructii civile in scopul limitarii degajarii', 'si propagarii focului si fumului.']
};
const detectUsage = name => {
  const n = String(name || '').toUpperCase();
  if (n.includes('N2XH')) return USAGE.n2xh;
  if (n.includes('RVK')) return USAGE.rvk;
  if (n.includes('MYYM') || ['MYF', 'FY', 'A05VV', 'H05VV', 'H07V'].some(t => n.includes(t))) return USAGE.casnic;
  return USAGE.energie; // CYYF, CYABYF + default
};

// operator full name → initials, e.g. "Gupta Roshan" → "G.R."
const initials = name => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts.map(p => p[0].toUpperCase() + '.').join('') : '-';
};

// cosmetic faux-QR (real one is printed by the Zebra) — deterministic from payload
function FauxQR({ payload = '', px = 100 }) {
  const N = 21;
  const cells = [];
  let h = 2166136261;
  for (let i = 0; i < payload.length; i++) { h ^= payload.charCodeAt(i); h = Math.imul(h, 16777619); }
  for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
    const inFinder = (r < 7 && c < 7) || (r < 7 && c >= N - 7) || (r >= N - 7 && c < 7);
    let on;
    if (inFinder) {
      const oR = r < 7 ? 0 : N - 7, oC = c < 7 ? 0 : N - 7;
      const m = Math.max(Math.abs(r - oR - 3), Math.abs(c - oC - 3));
      on = m === 3 || m <= 1;
    } else {
      let x = Math.imul((h ^ (r * 131 + c * 977)) >>> 0, 2654435761); x ^= x >>> 15;
      on = ((x >>> 0) % 100) > 52;
    }
    cells.push(on);
  }
  return (
    <div className="lbl-qr" style={{ width: px, height: px, gridTemplateColumns: `repeat(${N},1fr)`, gridTemplateRows: `repeat(${N},1fr)` }}>
      {cells.map((on, i) => <span key={i} style={{ background: on ? '#000' : '#fff' }} />)}
    </div>
  );
}

// render a spec (array of elements in label-dot coords) scaled to widthPx
function LabelCanvas({ elements, widthPx = 360 }) {
  const scale = widthPx / LABEL_W;
  return (
    <div className="lbl-canvas" style={{ width: widthPx, height: LABEL_H * scale }}>
      {elements.map((el, i) => {
        const base = { position: 'absolute', left: el.x * scale, top: el.y * scale };
        if (el.type === 'line')
          return <div key={i} style={{ ...base, width: (el.w || LABEL_W) * scale, height: Math.max(1, (el.h || 2) * scale), background: '#000' }} />;
        if (el.type === 'box')
          return <div key={i} style={{ ...base, width: el.w * scale, height: el.h * scale, border: `${Math.max(1, (el.t || 2) * scale)}px solid #000`, boxSizing: 'border-box' }} />;
        if (el.type === 'qr')
          return <div key={i} style={base}><FauxQR payload={el.payload} px={el.s * scale} /></div>;
        if (el.type === 'logo')
          return el.src
            ? <img key={i} src={el.src} alt="logo" style={{ ...base, width: el.w * scale, height: el.h * scale, objectFit: 'contain' }} />
            : <div key={i} className="lbl-logo-ph" style={{ ...base, width: el.w * scale, height: el.h * scale, fontSize: el.h * 0.5 * scale }}>{el.text || 'LOGO'}</div>;
        return (
          <div key={i} style={{ ...base, width: el.w ? el.w * scale : undefined, fontSize: (el.size || 20) * scale, lineHeight: 1, fontWeight: el.bold ? 700 : 400, fontFamily: 'monospace', color: '#000', whiteSpace: 'nowrap', textAlign: el.align || 'left' }}>
            {el.text}
          </div>
        );
      })}
    </div>
  );
}

// Drum label v2 — framed, tabular layout: logo+CE header, centered SORTIMENT band,
// bordered spec grid, boxed Utilizare, prominent LUNGIME band, production row, footer.
function drumLabelSpec(data, { executedLen = 0, operator = '', lot = '', logoGfa = null, ceGfa = null } = {}) {
  const d = data || {};
  const st = d.standard || {};
  const els = [];
  const sortiment = d.name ? formatProductLabel(d) : d.productLabel || 'N/A';
  const usage = detectUsage(d.name);
  const productStd = st.productStandard || '-';
  const perfStd = st.performanceStandard || '-';
  const tension = st.tension || '0.6/1kV';
  const fire = st.fireReaction || '-';
  const nb = st.notifiedBody || '-';
  const dop = st.dop || '-';
  const year = new Date().getFullYear();
  const lotStr = lot || `${d.jobCode || '-'} / ${year}`;
  const lengthStr = `${Number(executedLen).toFixed(0)} ml +/- 2%`;

  const color = d.color || '';

  // outer frame
  els.push({ type: 'box', x: 6, y: 6, w: 800, h: 597, t: 3 });

  // header: logo (left) + QR → project-details (center) + CE (right)
  const jobUrl = `${APP_BASE}/apps/project-management/project-details/${d.jobId || ''}`;
  els.push({ type: 'logo', x: 22, y: 14, src: logoFinal, gfa: logoGfa, w: 178, h: 121 });
  els.push({ type: 'qr', x: 405, y: 16, s: 100, mag: 2, payload: jobUrl });
  els.push({ type: 'text', x: 340, y: 122, w: 230, align: 'center', size: 11, text: 'scan → deschide jobul' });
  els.push({ type: 'logo', x: 700, y: 42, src: ceMarking, gfa: ceGfa, w: 96, h: 68 });
  els.push({ type: 'line', x: 6, y: 144, w: 800, h: 3 });

  // SORTIMENT band (centered headline, with color)
  els.push({ type: 'text', x: 6, y: 160, w: 800, align: 'center', size: 34, bold: true, text: `SORTIMENT: ${sortiment}${color ? ` · ${color}` : ''}` });
  els.push({ type: 'line', x: 6, y: 200, w: 800, h: 2 });

  // spec grid (bordered table)
  const tY = 208, tH = 132, colX = 406;
  els.push({ type: 'box', x: 14, y: tY, w: 784, h: tH, t: 2 });
  els.push({ type: 'line', x: 14, y: tY + 33, w: 784, h: 1 });
  els.push({ type: 'line', x: 14, y: tY + 66, w: 784, h: 1 });
  els.push({ type: 'line', x: 14, y: tY + 99, w: 784, h: 1 });
  els.push({ type: 'line', x: colX, y: tY + 66, w: 1, h: 66 });
  els.push({ type: 'text', x: 26, y: tY + 9, size: 18, text: `STANDARD PRODUS: ${productStd}` });
  els.push({ type: 'text', x: 26, y: tY + 42, size: 18, text: `STANDARD PERFORMANTA: ${perfStd}` });
  els.push({ type: 'text', x: 26, y: tY + 75, size: 18, text: `DOP: ${dop}` });
  els.push({ type: 'text', x: colX + 14, y: tY + 75, size: 18, text: `N.B.: ${nb}` });
  els.push({ type: 'text', x: 26, y: tY + 108, size: 18, text: `U0/U: ${tension}` });
  els.push({ type: 'text', x: colX + 14, y: tY + 108, size: 18, text: `Reactie la foc: ${fire}` });

  // Utilizare (left) + JOB ID / TOKEN ID fallback (right)
  const uY = tY + tH + 8, uH = 100, uSplit = 512;
  els.push({ type: 'box', x: 14, y: uY, w: 784, h: uH, t: 2 });
  els.push({ type: 'line', x: uSplit, y: uY, w: 1, h: uH });
  els.push({ type: 'text', x: 26, y: uY + 8, size: 16, bold: true, text: 'Utilizare:' });
  let uy = uY + 30;
  usage.forEach(l => { els.push({ type: 'text', x: 26, y: uy, size: 14, text: l }); uy += 18; });
  els.push({ type: 'text', x: uSplit + 12, y: uY + 8, size: 13, bold: true, text: 'JOB ID:' });
  els.push({ type: 'text', x: uSplit + 12, y: uY + 26, size: 12, text: d.jobId || '-' });
  els.push({ type: 'text', x: uSplit + 12, y: uY + 54, size: 13, bold: true, text: 'TOKEN ID:' });
  els.push({ type: 'text', x: uSplit + 12, y: uY + 72, size: 12, text: d.tokenId || '-' });

  // LUNGIME band (prominent)
  const lY = uY + uH + 8;
  els.push({ type: 'box', x: 14, y: lY, w: 784, h: 46, t: 3 });
  els.push({ type: 'text', x: 6, y: lY + 10, w: 800, align: 'center', size: 30, bold: true, text: `LUNGIME: ${lengthStr}` });

  // production row: LOT | EXECUTAT | COD FPP
  const pY = lY + 54;
  els.push({ type: 'box', x: 14, y: pY, w: 784, h: 44, t: 2 });
  els.push({ type: 'line', x: 290, y: pY, w: 1, h: 44 });
  els.push({ type: 'line', x: 566, y: pY, w: 1, h: 44 });
  els.push({ type: 'text', x: 26, y: pY + 13, size: 18, text: `LOT: ${lotStr}` });
  els.push({ type: 'text', x: 304, y: pY + 13, size: 18, text: `EXECUTAT: ${initials(operator)}` });
  els.push({ type: 'text', x: 578, y: pY + 15, size: 14, text: 'COD FPP 05_1.0._13' });

  // footer
  els.push({ type: 'text', x: 6, y: pY + 54, w: 800, align: 'center', size: 13, text: 'Energoplast · 420005 Bistrita, Agronomului H 12 · tel/fax +40 263 228024 · www.energoplastbn.ro' });
  return els;
}

// Flange label — traceability: framed, logo+CE header, a BIG QR (ENG:DRUM:<token>
// scanned at warehouse reception) on the left + the identity fields on the right.
function flangeLabelSpec(data, { executedLen = 0, operator = '', lot = '', logoGfa = null, ceGfa = null } = {}) {
  const d = data || {};
  const els = [];
  const sortiment = d.name ? formatProductLabel(d) : d.productLabel || 'N/A';
  const color = d.color || '';
  const year = new Date().getFullYear();
  const lotStr = lot || `${d.jobCode || '-'} / ${year}`;
  const qr = d.tokenId ? `ENG:DRUM:${d.tokenId}` : d.jobCode ? `ENG:JOB:${d.jobCode}` : '';

  // outer frame
  els.push({ type: 'box', x: 6, y: 6, w: 800, h: 597, t: 3 });

  // header: logo (left) + title (center) + CE (right)
  els.push({ type: 'logo', x: 22, y: 14, src: logoFinal, gfa: logoGfa, w: 178, h: 121 });
  els.push({ type: 'logo', x: 700, y: 42, src: ceMarking, gfa: ceGfa, w: 96, h: 68 });
  els.push({ type: 'text', x: 200, y: 40, w: 500, align: 'center', size: 30, bold: true, text: 'ETICHETA DE FLANSA' });
  els.push({ type: 'text', x: 200, y: 80, w: 500, align: 'center', size: 15, text: 'trasabilitate / receptie depozit' });
  els.push({ type: 'line', x: 6, y: 144, w: 800, h: 3 });

  // divider between QR (left) and fields (right)
  els.push({ type: 'line', x: 392, y: 150, w: 1, h: 448 });

  // BIG QR (left)
  if (qr) {
    els.push({ type: 'qr', x: 46, y: 176, s: 300, mag: 9, payload: qr });
    els.push({ type: 'text', x: 20, y: 500, w: 360, align: 'center', size: 15, bold: true, text: 'Scaneaza la receptie' });
  }

  // identity fields (right)
  const fx = 408;
  let y = 176;
  els.push({ type: 'text', x: fx, y, size: 26, bold: true, text: `${sortiment}${color ? ` · ${color}` : ''}` }); y += 40;
  els.push({ type: 'text', x: fx, y, size: 17, bold: true, text: 'TOKEN ID:' }); y += 24;
  els.push({ type: 'text', x: fx, y, size: 15, text: d.tokenId || '-' }); y += 34;
  els.push({ type: 'text', x: fx, y, size: 17, bold: true, text: 'JOB ID:' }); y += 24;
  els.push({ type: 'text', x: fx, y, size: 15, text: d.jobId || '-' }); y += 36;
  els.push({ type: 'text', x: fx, y, size: 18, text: `JOB COD: ${d.jobCode || '-'}` }); y += 30;
  els.push({ type: 'text', x: fx, y, size: 18, text: `LOT: ${lotStr}` }); y += 38;
  els.push({ type: 'text', x: fx, y, size: 26, bold: true, text: `LUNGIME: ${Number(executedLen).toFixed(0)} m` }); y += 36;
  els.push({ type: 'text', x: fx, y, size: 18, text: `EXECUTAT: ${initials(operator)}` });

  return els;
}

// ── ZPL generation from the SAME spec that drives the preview (so print == preview) ──
const zesc = s => String(s ?? '').replace(/[\^~]/g, '');

function specToZpl(elements) {
  let z = '^XA\n^CI28\n^PW812\n^LL609\n~SD20\n';
  for (const el of elements) {
    const x = Math.round(el.x), y = Math.round(el.y);
    if (el.type === 'text') {
      const s = Math.round(el.size || 20);
      if (el.align === 'center' && el.w) {
        z += `^FO${x},${y}^A0N,${s},${s}^FB${Math.round(el.w)},1,0,C^FD${zesc(el.text)}^FS\n`;
      } else {
        z += `^FO${x},${y}^A0N,${s},${s}^FD${zesc(el.text)}^FS\n`;
      }
    } else if (el.type === 'line') {
      const w = Math.max(1, Math.round(el.w || LABEL_W)), h = Math.max(1, Math.round(el.h || 2));
      z += `^FO${x},${y}^GB${w},${h},${Math.max(1, Math.min(w, h))}^FS\n`;
    } else if (el.type === 'box') {
      z += `^FO${x},${y}^GB${Math.round(el.w)},${Math.round(el.h)},${Math.round(el.t || 2)}^FS\n`;
    } else if (el.type === 'qr') {
      const mag = el.mag || Math.max(2, Math.min(10, Math.round((el.s || 150) / 33)));
      z += `^FO${x},${y}^BQN,2,${mag}^FDMA,${zesc(el.payload)}^FS\n`;
    } else if (el.type === 'logo' && el.gfa) {
      z += `^FO${x},${y}${el.gfa}^FS\n`;
    } else if (el.type === 'logo' && el.text) {
      const s = Math.round(el.h * 0.5);
      z += `^FO${x},${y}^A0N,${s},${s}^FD${zesc(el.text)}^FS\n`; // CE placeholder until a PNG
    }
  }
  return z + '\n^XZ';
}

// convert an <img> to a monochrome ZPL ^GFA graphic (threshold, 1=black)
function imageToGfa(img, widthDots, threshold = 0.68) {
  const w = Math.max(1, Math.round(widthDots));
  const h = Math.max(1, Math.round(w * (img.naturalHeight / img.naturalWidth)));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  const bytesPerRow = Math.ceil(w / 8);
  const total = bytesPerRow * h;
  let hex = '';
  for (let py = 0; py < h; py++) {
    for (let bx = 0; bx < bytesPerRow; bx++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const px = bx * 8 + bit;
        let black = 0;
        if (px < w) {
          const idx = (py * w + px) * 4;
          const a = data[idx + 3];
          const lum = a < 128 ? 1 : (0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2]) / 255;
          black = lum < threshold ? 1 : 0;
        }
        byte = (byte << 1) | black;
      }
      hex += byte.toString(16).padStart(2, '0').toUpperCase();
    }
  }
  return { gfa: `^GFA,${total},${total},${bytesPerRow},${hex}`, w, h };
}

function LabelFields({ lot, setLot, f2, setF2 }) {
  return (
    <div className="label-fields">
      <div className="lf-row">
        <label>LOT (manual)</label>
        <input value={lot} onChange={e => setLot(e.target.value)} placeholder="ex: 12345 / 2026" />
      </div>
      <div className="lf-row">
        <label>Câmp 2 (de definit)</label>
        <input value={f2} onChange={e => setF2(e.target.value)} placeholder="—" />
      </div>
    </div>
  );
}

function LabelPreviewPanel({ data, executedLen, operator, lot, logoGfa, ceGfa }) {
  const [tab, setTab] = useState('drum');
  const [printing, setPrinting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const opts = { executedLen, operator, lot, logoGfa, ceGfa };
  const elements = tab === 'drum' ? drumLabelSpec(data, opts) : flangeLabelSpec(data, opts);

  const testPrint = async () => {
    if (printing) return;
    setPrinting(true);
    setFeedback(null);
    try {
      const zpl = specToZpl(elements);
      await api.rawPrint({ zpl });
      setFeedback({ type: 'success', msg: `Trimis la imprimantă (${zpl.length} bytes)` });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message });
    } finally {
      setPrinting(false);
      setTimeout(() => setFeedback(null), 5000);
    }
  };

  return (
    <div className="lbl-preview">
      <div className="lbl-tabs">
        <button className={tab === 'drum' ? 'on' : ''} onClick={() => setTab('drum')}>Eticheta TAMBUR</button>
        <button className={tab === 'flange' ? 'on' : ''} onClick={() => setTab('flange')}>Eticheta FLANSA</button>
      </div>
      <LabelCanvas elements={elements} widthPx={640} />
      <div className="lbl-note">
        {tab === 'drum'
          ? 'Preview live · eticheta de pe tambur (QR antet → deschide jobul)'
          : 'Preview live · eticheta de flanșă · QR mare = ENG:DRUM (scan la recepție)'}
      </div>
      <button className="btn-testprint" onClick={testPrint} disabled={printing}>
        {printing ? 'SE PRINTEAZĂ…' : `TEST PRINT — ${tab === 'drum' ? 'TAMBUR' : 'FLANSA'} (fără completare)`}
      </button>
      {feedback && <div className={`feedback ${feedback.type}`}>{feedback.msg}</div>}
    </div>
  );
}

const MACHINES = ['l120', 'l70', 'l75'];

export default function L120PrintingLabels() {
  const [machineId, setMachineId] = useState('l120');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operator, setOperator] = useState('');
  const [adjustment, setAdjustment] = useState(0);
  const [lotManual, setLotManual] = useState('');
  const [field2, setField2] = useState('');
  const [logoGfa, setLogoGfa] = useState(null);
  const [ceGfa, setCeGfa] = useState(null);

  // convert the logo + CE mark → monochrome ZPL ^GFA once (for the physical print).
  // Width in dots must match the label spec's element width so print == preview.
  useEffect(() => {
    const load = (src, widthDots, set) => {
      const img = new Image();
      img.onload = () => { try { set(imageToGfa(img, widthDots).gfa); } catch (e) { console.error('img → GFA failed:', e); } };
      img.src = src;
    };
    load(logoFinal, 178, setLogoGfa);
    load(ceMarking, 96, setCeGfa);
  }, []);

  const { meterAct, speed, status, connected } = useMachineSocket(machineId);

  const fetchStatus = useCallback(async () => {
    try {
      setError(null);
      const res = await api.getLabelStatus(machineId);
      setData(res);
      if (res.operator && !operator) setOperator(res.operator);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [machineId, operator]);

  useEffect(() => {
    setLoading(true);
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handlePrinted = () => {
    setAdjustment(0);
    setTimeout(fetchStatus, 500);
  };

  const executedLen = meterAct + adjustment;

  return (
    <div className="labelstation-root">
      <div className="app">
        <header className="app-header">
          <div className="header-left">
            <h1>Label Station</h1>
            <select
              className="machine-pick"
              value={machineId}
              onChange={e => setMachineId(e.target.value)}
            >
              {MACHINES.map(m => (
                <option key={m} value={m}>
                  {m.toUpperCase()}
                </option>
              ))}
            </select>
            <span className={`ws-status ${connected ? 'on' : 'off'}`}>
              {connected ? 'LIVE' : 'OFFLINE'}
            </span>
            <span className={`machine-status ${status === 'running' ? 'running' : 'stopped'}`}>
              {status || 'N/A'}
            </span>
          </div>
          <OperatorSelect value={operator} onChange={setOperator} />
        </header>

        {error && <div className="error-bar">API Error: {error}</div>}

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <main className="app-main">
            <div className="ls-cols">
              <div className="ls-left">
                <ProductionInfo data={data} />
                <MeterCounter
                  meterAct={meterAct}
                  adjustment={adjustment}
                  setAdjustment={setAdjustment}
                  speed={speed}
                />
                <LabelFields lot={lotManual} setLot={setLotManual} f2={field2} setF2={setField2} />
                <PrintButton
                  machineId={machineId}
                  executedLen={executedLen}
                  operator={operator}
                  hasLastLabel={data?.hasLastLabel}
                  disabled={!data?.active || !operator}
                  onPrinted={handlePrinted}
                />
                <QueuePreview items={data?.queuePreview || []} />
              </div>
              <div className="ls-right">
                <LabelPreviewPanel
                  data={data}
                  executedLen={executedLen}
                  operator={operator}
                  lot={lotManual}
                  logoGfa={logoGfa}
                  ceGfa={ceGfa}
                />
              </div>
            </div>
          </main>
        )}
      </div>
    </div>
  );
}
